import React from 'react';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { Card } from '@frontend/shadcn/components/ui/card';
import { ChevronLeftIcon, ChevronRightIcon } from '@revfluence/fresh-icons/solid';
import { DiscoverInstagramCreatorsQuery } from '@frontend/gql/social/graphql';
import {
  TableHeader,
  TableHeaderSection,
  TableHeaderLayout,
  TableHeaderLabel,
  TableHeaderIconButton,
} from '../../Analytics/components/table-header';
import { ColumnDefinitionInput } from '../components/table/types';
import { InstagramCreatorSearchTable } from './components/table';
import { useInstagramCreatorSearchTable } from './components/table/hooks/useInstagramCreatorSearchTable';
import { Spinner } from '../../Analytics/components/ui/spinner';

interface InstagramCreatorSearchTabProps {
  data: DiscoverInstagramCreatorsQuery | undefined;
  isLoading: boolean;
  limit: number;
  setSelectedColumns: (
    columns: ColumnDefinitionInput[] | ((prev: ColumnDefinitionInput[]) => ColumnDefinitionInput[]),
  ) => void;
  selectedColumns: ColumnDefinitionInput[];
  onPageChange: (cursor: string | null, direction: 'next' | 'prev') => void;
  isPaginationFetching: boolean;
}

export const InstagramCreatorSearchTab = ({
  data,
  isLoading,
  limit,
  setSelectedColumns,
  selectedColumns,
  onPageChange,
  isPaginationFetching
}: InstagramCreatorSearchTabProps) => {
  const instagramCreatorsData = data?.discoverInstagramCreators?.creators ?? [];
  const paging = data?.discoverInstagramCreators?.paging ?? { cursors: { after: null, before: null }, hasNext: false, hasPrevious: false };
  const { cursors = { after: null, before: null }, hasNext = false, hasPrevious = false } = paging;
  const { after = null, before = null } = cursors;
  const totalCount = instagramCreatorsData?.length ?? 0;

  const { instagramTableProps } = useInstagramCreatorSearchTable({
    selectedColumns,
    setSelectedColumns,
    data: instagramCreatorsData,
    currentPage: limit,
    pageSize: 10,
    handleSortingChange: () => { console.log('handleSortingChange'); },
    sorting: [],
    globalFilter: '',
    setIsPartialLoading: () => { console.log('setIsPartialLoading'); },
  });

  const handlePrevPage = () => {
    if (before) {
      onPageChange(before, 'prev');
    }
  };

  const handleNextPage = () => {
    if (after) {
      onPageChange(after, 'next');
    }
  };

  return (
    <>
      {isLoading && !isPaginationFetching ? (
        <div className="mx-4 mb-4">
          <Skeleton className="h-[110px] rounded-xl mb-4" aria-label="Loading creator search" />
          <Skeleton className="h-[400px] rounded-xl" aria-label="Loading results table" />
        </div>
      ) : (
        <Card className="mx-4 mb-4" aria-label="Instagram Creators Search table">
          <TableHeader>
            <TableHeaderLayout>
              <TableHeaderSection>
                <TableHeaderLabel>
                  <div className="flex items-center gap-2">
                    <span aria-live="polite">{totalCount} Creators</span>
                    {(isLoading || isPaginationFetching) && <Spinner size="sm" spinning={isLoading || isPaginationFetching} />}
                  </div>
                </TableHeaderLabel>
              </TableHeaderSection>
              <TableHeaderSection>
                {/* <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <TableHeaderIconButton
                      disabled={isAnyExporting || memberInsights?.length === 0}
                      data-dd-action-name="export-members-data"
                      aria-label={isAnyExporting ? 'Export in progress...' : 'Export member data'}
                      aria-busy={isAnyExporting}
                    >
                      <div className="flex items-center">
                        <ArrowDownToLineIcon className="h-4 w-4" aria-hidden="true" />
                      </div>
                    </TableHeaderIconButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => handleExport('current')}
                      disabled={isAnyExporting}
                      data-dd-action-name="export-current-view"
                      aria-label="Export current view of member data"
                    >
                      Export current view
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleExport('full')}
                      disabled={isAnyExporting}
                      data-dd-action-name="export-full-data"
                      aria-label="Export all member data fields"
                    >
                      Export all fields
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu> */}
                {/* <ColumnDialog {...columnDialogProps}>
                  <TableHeaderIconButton
                    data-dd-action-name="open-column-selector"
                    aria-label="Select columns to display"
                  >
                    <ColumnsIconV3 size={16} aria-hidden="true" />
                  </TableHeaderIconButton>
                </ColumnDialog> */}
                <nav aria-label="Table navigation" className="flex items-center">
                  <TableHeaderIconButton
                    onClick={handlePrevPage}
                    disabled={!hasPrevious || isPaginationFetching || isLoading}
                    data-dd-action-name="members-table-previous-page"
                    aria-label="Previous page"
                    aria-disabled={!hasPrevious || isPaginationFetching || isLoading}
                  >
                    <ChevronLeftIcon aria-hidden="true" />
                  </TableHeaderIconButton>
                  {/* <TableHeaderPagination
                    currentPage={currentPage}
                    limit={limit}
                    totalCount={totalCount}
                    aria-label={`Page ${currentPage + 1} of results`}
                  /> */}
                  <TableHeaderIconButton
                    onClick={handleNextPage}
                    disabled={!hasNext || isPaginationFetching || isLoading}
                    data-dd-action-name="members-table-next-page"
                    aria-label="Next page"
                    aria-disabled={!hasNext || isPaginationFetching || isLoading}
                  >
                    <ChevronRightIcon aria-hidden="true" />
                  </TableHeaderIconButton>
                </nav>
              </TableHeaderSection>
            </TableHeaderLayout>
          </TableHeader>
          {
            isPaginationFetching ? (
              <div className="mx-4 mb-4">
                <Skeleton className="h-[400px] rounded-xl" aria-label="Loading results table" />
              </div>
            ) : (
              <InstagramCreatorSearchTable
                {...instagramTableProps}
                updatingColumns={[]}
                aria-busy={isLoading}
                aria-label="Members data table"
              />
            )
          }
        </Card>
      )}
    </>
  );
};
